import { Component } from "@angular/core";
import { Router } from "@angular/router";
import { NotifierService } from "angular-notifier";
import {
  faSearch, faCalculator, faPrint, faSave, faChain
} from "@fortawesome/free-solid-svg-icons";
import { FormField } from "src/app/interfaces/form-field.model";
import { GstCalculationService } from "src/app/services/autopac/gst-calculation.service";

@Component({
  selector: "app-gst-calculation",
  templateUrl: "./gst-calculation.component.html",
  styleUrls: ["./gst-calculation.component.css"]
})

export class GstCalculationComponent {
  faSearch = faSearch;
  faCalculator = faCalculator;
  faPrint = faPrint;
  faSave = faSave;
  faChain = faChain;
  showSaveConfirmation = false;
  private pendingSavePayload: any = null;

  constructor(
    private gstService: GstCalculationService,
    private router: Router,
    private notifier: NotifierService,
  ) {}

  private nullableNumberValue(form: FormField[], id: string): number | null {
    const value = form.find((field) => field.id === id)?.value;
    if (value === undefined || value === null || value === "") return null;
    const number = Number(value);
    return Number.isFinite(number) ? number : null;
  }

  gstSearchForm: FormField[] = [
    { type: 'search', id: 'ContractNumber', label: 'Contract Number', searchEvent: true, searchType: "searchContract"},
    { type: 'text', id: 'RegistrationDate', label: 'Registration Date', lock: true },
    { type: 'text', id: 'RegistrationNumber', label: 'Registration Number', lock: true },
  ];

  gstTotalAmountsForm: FormField[] = [
    { type: 'text', id: 'VehiclePrice', label: 'Vehicle Price', lock: true },
    { type: 'number', id: 'COETopUp', label: 'COE Top Up', lock: true, step: 0.01 },
    { type: 'number', id: 'AdditionalRoadTax', label: 'Additional Road Tax', step: 0.01 },
    { type: 'number', id: 'AdditionalRadioLicense', label: 'Additional Radio License', step: 0.01 },
    { type: 'number', id: 'COEDeposit', label: 'COE Deposit', step: 0.01 },
    { type: 'number', id: 'Accessories', label: 'Accessories', lock: true, step: 0.01 },
    { type: 'text', id: 'SubTotal1', label: 'Sub Total', lock: true },
  ];

  gstLessAmountsForm: FormField[] = [
    { type: 'number', id: 'RoadTaxRefund', label: 'Road Tax Refund', step: 0.01 },
    { type: 'number', id: 'RegistrationRebate', label: 'Registration Rebate', step: 0.01 },
    { type: 'number', id: 'CashDiscount', label: 'Cash Discount', step: 0.01 },
    { type: 'number', id: 'COESubsidy', label: 'COE Subsidy', step: 0.01 },
    { type: 'number', id: 'COERebate', label: 'COE Rebate', step: 0.01 },
    { type: 'number', id: 'DutyRebate', label: 'Duty Rebate', step: 0.01 },
    { type: 'number', id: 'ARFRebate', label: 'ARF Rebate', step: 0.01 },
    { type: 'number', id: 'CEVSRebate', label: 'CEVS Rebate', step: 0.01 },
    { type: 'number', id: 'CostOfCOE', label: 'Cost of COE', step: 0.01 },
    { type: 'number', id: 'RoadTax', label: 'Road Tax', step: 0.01 },
    { type: 'number', id: 'RadioLicense', label: 'Radio License', step: 0.01 },
    { type: 'number', id: 'Registration', label: 'Registration', step: 0.01 },
    { type: 'number', id: 'TransWeighingFee', label: 'Trans/Weighing Fee', step: 0.01 },
    { type: 'number', id: 'ARF', label: 'ARF', step: 0.01 },
    { type: 'number', id: 'CEVSVESSurcharge', label: 'CEVS/VES Surcharge', step: 0.01 },
    { type: 'number', id: 'COERebateLTA', label: 'COE Rebate LTA', step: 0.01 },
    { type: 'text', id: 'TotalAmountPayable', label: 'Total Amount Payable to LTA', lock: true },
  ];

  gstOthersForm: FormField[] = [
    { type: 'text', id: 'LessDescription', label: 'Less (Description)' },
    { type: 'number', id: 'LessAmount', label: 'Less (Amount)', step: 0.01 },
    { type: 'text', id: 'AddDescription', label: 'Add (Description)' },
    { type: 'number', id: 'AddAmount', label: 'Add (Amount)', step: 0.01 },
    { type: 'text', id: 'OthersSubTotal', label: 'Sub Total', lock: true }
  ];

  gstRebatesForm: FormField[] = [
    { type: 'number', id: 'PARFRebate', label: 'PARF Rebate', lock: true, step: 0.01 },
    { type: 'number', id: 'COEResidualRebate', label: 'COE Residual Rebate', lock: true, step: 0.01 },
    { type: 'number', id: 'OPCRebate', label: 'OPC Rebate', lock: true, step: 0.01 },
  ];

  gstFinalForm: FormField[] = [
    { type: 'text', id: 'CreditNote', label: 'Credit Note', lock: true },
    { type: 'text', id: 'InWords', label: 'In Words', lock: true },
    { type: 'number', id: 'GST5Base', label: 'Regrossed Taxable Amount Subject to 5% GST', lock: true, step: 0.01 },
    { type: 'text', id: 'GST5Payable', label: 'GST Payable at 5%', lock: true },
    { type: 'number', id: 'GST7Base', label: 'Regrossed Taxable Amount Subject to 7% GST', lock: true, step: 0.01 },
    { type: 'text', id: 'GST7Payable', label: 'GST Payable at 7%', lock: true },
    { type: 'number', id: 'GST8Base', label: 'Regrossed Taxable Amount Subject to 8% GST', lock: true, step: 0.01 },
    { type: 'text', id: 'GST8Payable', label: 'GST Payable at 8%', lock: true },
    { type: 'number', id: 'GST9Base', label: 'Regrossed Taxable Amount Subject to 9% GST', lock: true, step: 0.01 },
    { type: 'text', id: 'GST9Payable', label: 'GST Payable at 9%', lock: true },
    { type: 'text', id: 'FinalSubTotal', label: 'Sub Total', lock: true }
  ];

  InvoiceNumber = "";
  private surcharge: number | null = null;

  searchContract(_event?: { fieldId: string }): void {
  const contractNo = String(
    this.gstSearchForm.find((field) => field.id === "ContractNumber")?.value ?? "",
  ).trim();
  if (!contractNo) {
    this.notifier.notify("error", "Please enter a contract number.");
    return;
  }

  this.InvoiceNumber = "";
  this.surcharge = null;
  const clearOtherFields = (fields: FormField[]) => fields.map((field) =>
    field.id === "ContractNumber" ? field : { ...field, value: "" },
  );
  this.gstSearchForm = clearOtherFields(this.gstSearchForm);
  this.gstTotalAmountsForm = clearOtherFields(this.gstTotalAmountsForm);
  this.gstLessAmountsForm = clearOtherFields(this.gstLessAmountsForm);
  this.gstOthersForm = clearOtherFields(this.gstOthersForm);
  this.gstRebatesForm = clearOtherFields(this.gstRebatesForm);
  this.gstFinalForm = clearOtherFields(this.gstFinalForm);

  this.gstService.getDetailsByContractNo(contractNo).subscribe({
    next: (res) => {
      if (res?.statusCode !== 200 || (res?.code !== undefined && res.code !== 0)) {
        this.notifier.notify("error", res?.message || "Unable to retrieve contract data.");
        return;
      }
      const data = res?.body;
      if (!data || typeof data !== "object") {
        this.notifier.notify("error", res?.message || "Unable to retrieve contract data.");
        return;
      }
      this.InvoiceNumber = data.InvoiceNo ?? "";
      this.surcharge = data.Surcharge ?? null;

      const mapToForm = (form: FormField[], mapping: { [key: string]: string }) => {
        form.forEach(field => {
          const mappedKey = mapping[field.id];
          if (mappedKey && data[mappedKey] !== undefined) {
            field.value = data[mappedKey];
          }
        });
      };
  
        mapToForm(this.gstSearchForm, {
          RegistrationDate: 'RegisterDate',
          RegistrationNumber: 'RegisterNo'
        });
  
        mapToForm(this.gstTotalAmountsForm, {
          VehiclePrice: 'VehiclePrice',
          COETopUp: 'CoeTopUp',
          AdditionalRoadTax: 'AdditionalRoadTax',
          AdditionalRadioLicense: 'AdditionalRadioLicense',
          COEDeposit: 'CoeDeposit',
          Accessories: 'Accessories',
          SubTotal1: 'SubTotal'
        });
  
        mapToForm(this.gstLessAmountsForm, {
          RoadTaxRefund: 'RoadTaxRefund',
          RegistrationRebate: 'RegistrationRebate',
          CashDiscount: 'CashDiscount',
          COESubsidy: 'CoeSubsidy',
          COERebate: 'CoeRebate',
          DutyRebate: 'DutyRebate',
          ARFRebate: 'ArfRebate',
          CEVSRebate: 'CevsRebate',
          CostOfCOE: 'CostOfCOE',
          RoadTax: 'RoadTax',
          RadioLicense: 'RadioLicense',
          Registration: 'Registration',
          TransWeighingFee: 'TransWeighingFee',
          ARF: 'Arf',
          CEVSVESSurcharge: 'CevsSurcharge',
          COERebateLTA: 'CoeRebateLTA',
          TotalAmountPayable: 'TotalAmountPayableToLTA'
        });
  
        mapToForm(this.gstOthersForm, {
          LessDescription: 'LessDescription',
          LessAmount: 'LessAmount',
          AddDescription: 'AddDescription',
          AddAmount: 'AddAmount',
          OthersSubTotal: 'SubTotalNonGst'
        });
  
        mapToForm(this.gstRebatesForm, {
          PARFRebate: 'ParfRebate',
          COEResidualRebate: 'CoeResidualRebate',
          OPCRebate: 'OpcRebate'
        });
  
        mapToForm(this.gstFinalForm, {
          CreditNote: 'CreditNote',
          InWords: 'CreditInWords',
          GST5Base: 'RegrossedTaxableAmount5GST',
          GST5Payable: 'GtsPayable5',
          GST7Base: 'RegrossedTaxableAmount7GST',
          GST7Payable: 'GtsPayable7',
          GST8Base: 'RegrossedTaxableAmount8GST',
          GST8Payable: 'GtsPayable8',
          GST9Base: 'RegrossedTaxableAmount9GST',
          GST9Payable: 'GtsPayable9',
          FinalSubTotal: 'SubTotalFinal'
        });
      },
      error: (err) => {
        this.notifier.notify("error", "An error occurred while retrieving contract data.");
      }
    });
  }
  
  updateForm(event: any): void {
    const formIdMapping: { [key: string]: string } = {
      COEDeposit: "CoeDeposit",
      COESubsidy: "CoeSubsidy",
      COERebate: "CoeRebate",
      ARFRebate: "ArfRebate",
      CEVSRebate: "CevsRebate",
      ARF: "Arf",
      CEVSVESSurcharge: "CevsSurcharge",
      COERebateLTA: "CoeRebateLTA"
    };
  
    const updatedIndex = event?.updatedValue;
    const formArray = event?.fields;
    const rawFieldName = formArray?.[updatedIndex]?.id;
    if (!rawFieldName) return;
    const fieldName = formIdMapping[rawFieldName] ?? rawFieldName;
    const recalculatedFields = new Set([
      "AdditionalRoadTax",
      "CoeDeposit",
      "CoeTopUp",
      "AdditionalRadioLicense",
      "Accessories",
      "RoadTaxRefund",
      "CashDiscount",
      "CoeRebate",
      "ArfRebate",
      "CostOfCOE",
      "RadioLicense",
      "TransWeighingFee",
      "CevsSurcharge",
      "RegistrationRebate",
      "CoeSubsidy",
      "DutyRebate",
      "RoadTax",
      "Registration",
      "Arf",
      "LessAmount",
      "AddAmount",
      "ParfRebate",
      "CoeResidualRebate",
    ]);
    if (!recalculatedFields.has(fieldName)) return;
  
    const getValue = (form: FormField[], id: string) => {
      return form.find(f => f.id === id)?.value ?? null;
    };
  
    const payload = {
      data: {
        __type: "KAPS.Model.Autopac.CrmGstCalculation",
        ContractNo: getValue(this.gstSearchForm, 'ContractNumber'),
        RegisterNo: getValue(this.gstSearchForm, 'RegistrationNumber'),
        RegisterDate: getValue(this.gstSearchForm, 'RegistrationDate'),
        VehiclePrice: Number(getValue(this.gstTotalAmountsForm, 'VehiclePrice')) || 0,
        CoeTopUp: Number(getValue(this.gstTotalAmountsForm, 'COETopUp')) || 0,
        AdditionalRoadTax: Number(getValue(this.gstTotalAmountsForm, 'AdditionalRoadTax')) || 0,
        AdditionalRadioLicense: Number(getValue(this.gstTotalAmountsForm, 'AdditionalRadioLicense')) || 0,
        CoeDeposit: Number(getValue(this.gstTotalAmountsForm, 'COEDeposit')) || 0,
        Accessories: Number(getValue(this.gstTotalAmountsForm, 'Accessories')) || 0,
        SubTotal: Number(getValue(this.gstTotalAmountsForm, 'SubTotal1')) || 0,
  
        RoadTaxRefund: Number(getValue(this.gstLessAmountsForm, 'RoadTaxRefund')) || 0,
        RegistrationRebate: Number(getValue(this.gstLessAmountsForm, 'RegistrationRebate')) || 0,
        CashDiscount: Number(getValue(this.gstLessAmountsForm, 'CashDiscount')) || 0,
        CoeSubsidy: Number(getValue(this.gstLessAmountsForm, 'COESubsidy')) || 0,
        CoeRebate: Number(getValue(this.gstLessAmountsForm, 'COERebate')) || 0,
        DutyRebate: Number(getValue(this.gstLessAmountsForm, 'DutyRebate')) || 0,
        ArfRebate: Number(getValue(this.gstLessAmountsForm, 'ARFRebate')) || 0,
        CevsRebate: Number(getValue(this.gstLessAmountsForm, 'CEVSRebate')) || 0,
        CostOfCOE: Number(getValue(this.gstLessAmountsForm, 'CostOfCOE')) || 0,
        RoadTax: Number(getValue(this.gstLessAmountsForm, 'RoadTax')) || 0,
        RadioLicense: this.nullableNumberValue(this.gstLessAmountsForm, "RadioLicense"),
        Registration: Number(getValue(this.gstLessAmountsForm, 'Registration')) || 0,
        TransWeighingFee: Number(getValue(this.gstLessAmountsForm, 'TransWeighingFee')) || 0,
        Arf: Number(getValue(this.gstLessAmountsForm, 'ARF')) || 0,
        TotalAmountPayableToLTA: Number(getValue(this.gstLessAmountsForm, 'TotalAmountPayable')) || 0,
        CevsSurcharge: this.nullableNumberValue(this.gstLessAmountsForm, "CEVSVESSurcharge"),
        CoeRebateLTA: Number(getValue(this.gstLessAmountsForm, 'COERebateLTA')) || 0,
  
        LessDescription: getValue(this.gstOthersForm, 'LessDescription'),
        LessAmount: Number(getValue(this.gstOthersForm, 'LessAmount')) || 0,
        AddDescription: getValue(this.gstOthersForm, 'AddDescription'),
        AddAmount: Number(getValue(this.gstOthersForm, 'AddAmount')) || 0,
        SubTotalNonGst: Number(getValue(this.gstOthersForm, 'OthersSubTotal')) || 0,
  
        ParfRebate: Number(getValue(this.gstRebatesForm, 'PARFRebate')) || 0,
        CoeResidualRebate: Number(getValue(this.gstRebatesForm, 'COEResidualRebate')) || 0,
        OpcRebate: Number(getValue(this.gstRebatesForm, 'OPCRebate')) || 0,
  
        CreditNote: Number(getValue(this.gstFinalForm, 'CreditNote')) || 0,
        CreditInWords: getValue(this.gstFinalForm, 'InWords'),
        SubTotalFinal: Number(getValue(this.gstFinalForm, 'FinalSubTotal')) || 0,
  
        RegrossedTaxableAmount5GST: Number(getValue(this.gstFinalForm, 'GST5Base')) || 0,
        GtsPayable5: Number(getValue(this.gstFinalForm, 'GST5Payable')) || 0,
        RegrossedTaxableAmount7GST: Number(getValue(this.gstFinalForm, 'GST7Base')) || 0,
        GtsPayable7: Number(getValue(this.gstFinalForm, 'GST7Payable')) || 0,
        RegrossedTaxableAmount8GST: Number(getValue(this.gstFinalForm, 'GST8Base')) || 0,
        GtsPayable8: Number(getValue(this.gstFinalForm, 'GST8Payable')) || 0,
        RegrossedTaxableAmount9GST: Number(getValue(this.gstFinalForm, 'GST9Base')) || 0,
        GtsPayable9: Number(getValue(this.gstFinalForm, 'GST9Payable')) || 0,
        Surcharge: this.surcharge,
        InvoiceNo: this.InvoiceNumber
      },
      fieldName
    };
  
    this.gstService.updateFields(payload.data, payload.fieldName).subscribe({
      next: (res) => {
        const result = res?.body;
        if (!result) return;
  
        const mapping: { [key: string]: string } = {
          ContractNo: 'ContractNumber',
          RegisterNo: 'RegistrationNumber',
          RegisterDate: 'RegistrationDate',
          VehiclePrice: 'VehiclePrice',
          CoeTopUp: 'COETopUp',
          AdditionalRoadTax: 'AdditionalRoadTax',
          AdditionalRadioLicense: 'AdditionalRadioLicense',
          CoeDeposit: 'COEDeposit',
          Accessories: 'Accessories',
          SubTotal: 'SubTotal1',
          RoadTaxRefund: 'RoadTaxRefund',
          RegistrationRebate: 'RegistrationRebate',
          CashDiscount: 'CashDiscount',
          CoeSubsidy: 'COESubsidy',
          CoeRebate: 'COERebate',
          DutyRebate: 'DutyRebate',
          ArfRebate: 'ARFRebate',
          CevsRebate: 'CEVSRebate',
          CostOfCOE: 'CostOfCOE',
          RoadTax: 'RoadTax',
          RadioLicense: 'RadioLicense',
          Registration: 'Registration',
          TransWeighingFee: 'TransWeighingFee',
          Arf: 'ARF',
          TotalAmountPayableToLTA: 'TotalAmountPayable',
          CevsSurcharge: 'CEVSVESSurcharge',
          CoeRebateLTA: 'COERebateLTA',
          LessDescription: 'LessDescription',
          AddDescription: 'AddDescription',
          LessAmount: 'LessAmount',
          AddAmount: 'AddAmount',
          SubTotalNonGst: 'OthersSubTotal',
          ParfRebate: 'PARFRebate',
          CoeResidualRebate: 'COEResidualRebate',
          OpcRebate: 'OPCRebate',
          CreditNote: 'CreditNote',
          CreditInWords: 'InWords',
          SubTotalFinal: 'FinalSubTotal',
          RegrossedTaxableAmount5GST: 'GST5Base',
          GtsPayable5: 'GST5Payable',
          RegrossedTaxableAmount7GST: 'GST7Base',
          GtsPayable7: 'GST7Payable',
          RegrossedTaxableAmount8GST: 'GST8Base',
          GtsPayable8: 'GST8Payable',
          RegrossedTaxableAmount9GST: 'GST9Base',
          GtsPayable9: 'GST9Payable'
        };
  
        const updateForm = (formArray: FormField[]) => {
          formArray.forEach(field => {
            const resultKey = Object.keys(mapping).find(key => mapping[key] === field.id);
            if (resultKey && result.hasOwnProperty(resultKey)) {
              field.value = result[resultKey];
            }
          });
        };
  
        updateForm(this.gstSearchForm);
        updateForm(this.gstTotalAmountsForm);
        updateForm(this.gstLessAmountsForm);
        updateForm(this.gstOthersForm);
        updateForm(this.gstRebatesForm);
        updateForm(this.gstFinalForm);
      },
      error: () => this.notifier.notify("error", "Unable to recalculate GST values."),
    });
  }
  
  saveRecord(): void {
    const contractNo = String(
      this.gstSearchForm.find((field) => field.id === "ContractNumber")?.value ?? "",
    ).trim();
    if (!contractNo) {
      this.notifier.notify("error", "Please enter a contract number before saving.");
      return;
    }
    const getValue = (form: FormField[], id: string) => {
      return form.find(f => f.id === id)?.value ?? null;
    };
  
    const payload = {
      model: {
        __type: "KAPS.Model.Autopac.CrmGstCalculation",
        ContractNo: getValue(this.gstSearchForm, 'ContractNumber'),
        RegisterNo: getValue(this.gstSearchForm, 'RegistrationNumber'),
        RegisterDate: getValue(this.gstSearchForm, 'RegistrationDate'),
        VehiclePrice: Number(getValue(this.gstTotalAmountsForm, 'VehiclePrice')) || 0,
        CoeTopUp: Number(getValue(this.gstTotalAmountsForm, 'COETopUp')) || 0,
        AdditionalRoadTax: Number(getValue(this.gstTotalAmountsForm, 'AdditionalRoadTax')) || 0,
        AdditionalRadioLicense: Number(getValue(this.gstTotalAmountsForm, 'AdditionalRadioLicense')) || 0,
        CoeDeposit: Number(getValue(this.gstTotalAmountsForm, 'COEDeposit')) || 0,
        Accessories: Number(getValue(this.gstTotalAmountsForm, 'Accessories')) || 0,
        SubTotal: Number(getValue(this.gstTotalAmountsForm, 'SubTotal1')) || 0,
  
        RoadTaxRefund: Number(getValue(this.gstLessAmountsForm, 'RoadTaxRefund')) || 0,
        RegistrationRebate: Number(getValue(this.gstLessAmountsForm, 'RegistrationRebate')) || 0,
        CashDiscount: Number(getValue(this.gstLessAmountsForm, 'CashDiscount')) || 0,
        CoeSubsidy: Number(getValue(this.gstLessAmountsForm, 'COESubsidy')) || 0,
        CoeRebate: Number(getValue(this.gstLessAmountsForm, 'COERebate')) || 0,
        DutyRebate: Number(getValue(this.gstLessAmountsForm, 'DutyRebate')) || 0,
        ArfRebate: Number(getValue(this.gstLessAmountsForm, 'ARFRebate')) || 0,
        CevsRebate: Number(getValue(this.gstLessAmountsForm, 'CEVSRebate')) || 0,
        CostOfCOE: Number(getValue(this.gstLessAmountsForm, 'CostOfCOE')) || 0,
        RoadTax: Number(getValue(this.gstLessAmountsForm, 'RoadTax')) || 0,
        RadioLicense: this.nullableNumberValue(this.gstLessAmountsForm, "RadioLicense"),
        Registration: Number(getValue(this.gstLessAmountsForm, 'Registration')) || 0,
        TransWeighingFee: Number(getValue(this.gstLessAmountsForm, 'TransWeighingFee')) || 0,
        Arf: Number(getValue(this.gstLessAmountsForm, 'ARF')) || 0,
        TotalAmountPayableToLTA: Number(getValue(this.gstLessAmountsForm, 'TotalAmountPayable')) || 0,
        CevsSurcharge: this.nullableNumberValue(this.gstLessAmountsForm, "CEVSVESSurcharge"),
        CoeRebateLTA: Number(getValue(this.gstLessAmountsForm, 'COERebateLTA')) || 0,
  
        LessDescription: getValue(this.gstOthersForm, 'LessDescription'),
        LessAmount: Number(getValue(this.gstOthersForm, 'LessAmount')) || 0,
        AddDescription: getValue(this.gstOthersForm, 'AddDescription'),
        AddAmount: Number(getValue(this.gstOthersForm, 'AddAmount')) || 0,
        SubTotalNonGst: Number(getValue(this.gstOthersForm, 'OthersSubTotal')) || 0,
  
        ParfRebate: Number(getValue(this.gstRebatesForm, 'PARFRebate')) || 0,
        CoeResidualRebate: Number(getValue(this.gstRebatesForm, 'COEResidualRebate')) || 0,
        OpcRebate: Number(getValue(this.gstRebatesForm, 'OPCRebate')) || 0,
  
        CreditNote: Number(getValue(this.gstFinalForm, 'CreditNote')) || 0,
        CreditInWords: getValue(this.gstFinalForm, 'InWords'),
        SubTotalFinal: Number(getValue(this.gstFinalForm, 'FinalSubTotal')) || 0,
  
        RegrossedTaxableAmount5GST: Number(getValue(this.gstFinalForm, 'GST5Base')) || 0,
        GtsPayable5: Number(getValue(this.gstFinalForm, 'GST5Payable')) || 0,
        RegrossedTaxableAmount7GST: Number(getValue(this.gstFinalForm, 'GST7Base')) || 0,
        GtsPayable7: Number(getValue(this.gstFinalForm, 'GST7Payable')) || 0,
        RegrossedTaxableAmount8GST: Number(getValue(this.gstFinalForm, 'GST8Base')) || 0,
        GtsPayable8: Number(getValue(this.gstFinalForm, 'GST8Payable')) || 0,
        RegrossedTaxableAmount9GST: Number(getValue(this.gstFinalForm, 'GST9Base')) || 0,
        GtsPayable9: Number(getValue(this.gstFinalForm, 'GST9Payable')) || 0,
        Surcharge: this.surcharge,
        InvoiceNo: this.InvoiceNumber
      }
    };
  
    if (this.InvoiceNumber) {
      this.pendingSavePayload = payload;
      this.showSaveConfirmation = true;
      return;
    }

    this.submitSave(payload);
  }

  cancelSaveConfirmation(): void {
    this.showSaveConfirmation = false;
    this.pendingSavePayload = null;
  }

  confirmSaveRecord(): void {
    const payload = this.pendingSavePayload;
    this.cancelSaveConfirmation();
    if (payload) this.submitSave(payload);
  }

  private submitSave(payload: any): void {
    this.gstService.saveGst(payload).subscribe({
      next: (res) => {
        if (res?.statusCode !== 200 || (res?.code !== undefined && res.code !== 0)) {
          this.notifier.notify("error", res?.message || "Unable to save GST calculation.");
          return;
        }
        this.notifier.notify("success", res?.message || "Saved successfully!");
      },
      error: () => this.notifier.notify("error", "Unable to save GST calculation."),
    });
  }
  
  printRecord(): void {
    const contractNo = this.gstSearchForm.find(f => f.id === 'ContractNumber')?.value;
  
    if (!contractNo || contractNo.trim() === '') {
      this.notifier.notify("error", "Please enter a contract number to print.");
      return;
    }
  
    this.gstService.printGst(contractNo).subscribe({
      next: (res) => {
        const url = res?.body?.Name;
        if (url) {
          window.open(url, '_blank');
        } else {
          this.notifier.notify("error", "Could not generate report. Please try again.");
        }
      },
      error: () => {
        this.notifier.notify("error", "Error occurred while printing.");
      }
    });
  }

  toInvoice(): void {
    const contractNo = String(
      this.gstSearchForm.find((field) => field.id === "ContractNumber")?.value ?? "",
    ).trim();
    this.router.navigate(["/autopac/invoice"], {
      queryParams: contractNo ? { contractno: contractNo } : {},
    });
  }
  
}
